import type { QuoteDocument } from '@kalos/kalos-rpc';

import { getQuoteDocumentTaxCost, getQuoteDocumentTaxRate } from './quoteDocumentPricing';

/** Minimal quote line fields used for approved proposal totals. */
export interface QuoteLinePriceLike {
  adjustment?: string;
  quotedQuantity?: number;
  quoteStatus?: number;
  /** When 1, this item is exempt from percentage discounts. */
  excludeInvoiceDiscount?: number;
}

export function lineExtendedPrice(line: QuoteLinePriceLike): number {
  const adjustment = line.adjustment || '0';
  const cleaned = adjustment.replace(/[$,]/g, '');
  const price = parseFloat(cleaned);
  const quantity = line.quotedQuantity || 1;
  if (isNaN(price)) return 0;
  return price * quantity;
}

/** Discount and tax fields stored on the quote document for proposals. */
export interface ProposalPricingInput {
  discount?: string;
  discountcost?: string;
  tax?: number;
  taxCost?: number;
}

export function proposalPricingFromQuoteDocument(
  doc: QuoteDocument | null | undefined,
): ProposalPricingInput | null {
  if (!doc) return null;
  return {
    discount: doc.discount,
    discountcost: doc.discountcost,
    tax: getQuoteDocumentTaxRate(doc),
    taxCost: getQuoteDocumentTaxCost(doc),
  };
}

export interface ApprovedProposalBreakdown {
  /** Sum of all line items (before any acceptance filtering). */
  proposedSubtotal: number;
  /** Sum of accepted (quoteStatus === 1) line items. */
  approvedSubtotal: number;
  /** Sum of explicitly declined (quoteStatus === 2) line items only. */
  declinedAmount: number;
  /** Discount amount calculated off approvedSubtotal. */
  discountOnApproved: number;
  /** approvedSubtotal − discountOnApproved + prorated tax (rounded to cents). */
  approvedTotal: number;
  /** Number of accepted line items. */
  approvedCount: number;
}

/**
 * Approved subtotal, then discount and tax aligned with {@link PastProposals} / property proposal cards:
 * percentage discount on approved subtotal, or flat discount prorated by approved share of full subtotal;
 * tax rate on after-discount approved amount, or flat tax prorated the same way.
 *
 * Also returns the full breakdown fields needed by the post-signature totals display:
 * proposedSubtotal, approvedSubtotal, declinedAmount, discountOnApproved.
 */
export function computeApprovedProposalTotal(
  lines: QuoteLinePriceLike[],
  pricing: ProposalPricingInput | null | undefined,
): ApprovedProposalBreakdown {
  const fullSubtotal = lines.reduce((sum, line) => sum + lineExtendedPrice(line), 0);
  const approvedItems = lines.filter((line) => line.quoteStatus === 1);
  const approvedSubtotal = approvedItems.reduce((sum, line) => sum + lineExtendedPrice(line), 0);
  const declinedItems = lines.filter((line) => line.quoteStatus === 2);
  const declinedAmount = declinedItems.reduce((sum, line) => sum + lineExtendedPrice(line), 0);

  if (approvedItems.length === 0) {
    return {
      approvedTotal: 0,
      approvedCount: 0,
      proposedSubtotal: Math.round(fullSubtotal * 100) / 100,
      approvedSubtotal: 0,
      declinedAmount: Math.round(declinedAmount * 100) / 100,
      discountOnApproved: 0,
    };
  }

  if (!pricing) {
    return {
      approvedTotal: Math.round(approvedSubtotal * 100) / 100,
      approvedCount: approvedItems.length,
      proposedSubtotal: Math.round(fullSubtotal * 100) / 100,
      approvedSubtotal: Math.round(approvedSubtotal * 100) / 100,
      declinedAmount: Math.round(declinedAmount * 100) / 100,
      discountOnApproved: 0,
    };
  }

  const discountPercent = parseFloat(pricing.discount || '0');
  const discountCost = parseFloat(pricing.discountcost || '0');

  // Items exempt from percentage discounts (flat discounts apply to all items).
  const approvedExcludedSubtotal =
    discountPercent > 0
      ? approvedItems.reduce(
          (sum, line) => (line.excludeInvoiceDiscount === 1 ? sum + lineExtendedPrice(line) : sum),
          0,
        )
      : 0;
  const approvedDiscountableSubtotal = approvedSubtotal - approvedExcludedSubtotal;

  // Excluded items from the full subtotal (used for tax proration denominator).
  const fullExcludedSubtotal =
    discountPercent > 0
      ? lines.reduce(
          (sum, line) => (line.excludeInvoiceDiscount === 1 ? sum + lineExtendedPrice(line) : sum),
          0,
        )
      : 0;
  const fullDiscountableSubtotal = fullSubtotal - fullExcludedSubtotal;

  const discountOnApproved =
    discountPercent > 0 ? approvedDiscountableSubtotal * (discountPercent / 100) : discountCost;

  const afterDiscount = Math.max(0, approvedSubtotal - discountOnApproved);

  const taxRate = parseFloat(String(pricing.tax ?? 0)) || 0;
  const taxCostFlat = parseFloat(String(pricing.taxCost ?? 0)) || 0;
  const fullDiscount =
    discountPercent > 0 ? fullDiscountableSubtotal * (discountPercent / 100) : discountCost;
  const afterFullDiscount = Math.max(0, fullSubtotal - fullDiscount);

  const taxOnApproved =
    taxRate > 0
      ? afterDiscount * (taxRate / 100)
      : afterFullDiscount > 0
        ? taxCostFlat * (afterDiscount / afterFullDiscount)
        : 0;

  const total = afterDiscount + taxOnApproved;
  return {
    approvedTotal: Math.round(total * 100) / 100,
    approvedCount: approvedItems.length,
    proposedSubtotal: Math.round(fullSubtotal * 100) / 100,
    approvedSubtotal: Math.round(approvedSubtotal * 100) / 100,
    declinedAmount: Math.round(declinedAmount * 100) / 100,
    discountOnApproved: Math.round(discountOnApproved * 100) / 100,
  };
}
